import { getSession, signOut } from "next-auth/react";
import { clearSessionCache } from "@/lib/session-cache";
import { setActingTenant } from "@/lib/acting-tenant";

const KEYCLOAK_ISSUER =
  process.env.NEXT_PUBLIC_AUTH_KEYCLOAK_ISSUER ??
  "http://localhost:8080/realms/cueshub";

/**
 * Clears the NextAuth session and ends the Keycloak SSO session.
 * Without the Keycloak logout step, sign-in immediately re-authenticates the user.
 *
 * Refresh / offline tokens are revoked server-side in Auth.js `events.signOut`
 * (see auth.ts + keycloak-revoke.ts) when `signOut()` runs — the refresh token
 * is never exposed to the browser.
 */
export const SIGNED_OUT_KEY = "cueshub.signedOut";

let signOutInProgress = false;

/** Set before Keycloak logout; sign-in page reads this (query params are not allowed on post_logout_redirect_uri). */
export function markSignedOut(): void {
  sessionStorage.setItem(SIGNED_OUT_KEY, "1");
}

/** Clears the signed-out latch when the user explicitly chooses to sign in again. */
export function clearSignedOut(): void {
  sessionStorage.removeItem(SIGNED_OUT_KEY);
}

/** Clears super-admin org context so the next login does not inherit it. */
function clearActingTenantContext(): void {
  setActingTenant(null);
}

export function clearClientAuthState(): void {
  clearSessionCache();
}

async function clearServerSessionIfPresent(): Promise<void> {
  const session = await getSession();
  if (!session) return;
  try {
    await signOut({ redirect: false });
  } catch {
    // Session may already be invalid; client redirect still proceeds.
  }
}

function redirectToSignIn(query = "sessionExpired=1"): void {
  const target = `/auth/signin?${query}`;
  if (window.location.pathname !== "/auth/signin") {
    window.location.href = target;
  }
}

/**
 * Clears the local app session and returns to the login page.
 * Used when the session is already invalid (e.g. refresh token expired).
 */
export async function signOutToLogin(): Promise<void> {
  if (signOutInProgress) return;
  signOutInProgress = true;

  markSignedOut();
  clearClientAuthState();
  await clearServerSessionIfPresent();
  redirectToSignIn();
}

/**
 * Redirect when NextAuth already reports unauthenticated — no server sign-out call.
 */
export function redirectUnauthenticatedToSignIn(): void {
  if (signOutInProgress) return;
  signOutInProgress = true;

  markSignedOut();
  clearClientAuthState();
  redirectToSignIn();
}

export async function signOutCompletely(): Promise<void> {
  if (signOutInProgress) return;
  signOutInProgress = true;

  const session = await getSession();
  const idToken = session?.idToken;

  if (session?.error === "RefreshTokenExpired" || !idToken) {
    markSignedOut();
    clearClientAuthState();
    clearActingTenantContext();
    await clearServerSessionIfPresent();
    redirectToSignIn();
    return;
  }

  markSignedOut();
  clearClientAuthState();
  clearActingTenantContext();
  try {
    await signOut({ redirect: false });
  } catch {
    // Continue to Keycloak logout even if local session clear failed.
  }
  // Must match Keycloak client post.logout.redirect.uris exactly (no query string).
  const postLogoutRedirect = `${window.location.origin}/auth/signin`;

  const clientId =
    process.env.NEXT_PUBLIC_AUTH_KEYCLOAK_ID ?? "dashboard";
  const logoutUrl = new URL(
    `${KEYCLOAK_ISSUER}/protocol/openid-connect/logout`
  );
  if (idToken) {
    logoutUrl.searchParams.set("id_token_hint", idToken);
  }
  logoutUrl.searchParams.set("client_id", clientId);
  logoutUrl.searchParams.set("post_logout_redirect_uri", postLogoutRedirect);
  window.location.href = logoutUrl.toString();
}
